// Path: src/pages/WorkshopPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { 
  getSlideDetail, getSysPrompts, getSlideVersions, 
  saveSlideHtml, uploadToOSS, getAdjacentSlide, 
  deleteSlideChatMessage, getSlideCloudResources, deleteOSSFile, getSlideContext,
  createNewSlide, clearSlideFields, auditSlide,
  type SlideDetail, type SlideVersionInfo, type CloudResource, type SlideContextResponse 
} from '../lib/api';
import { extractHtmlCodeBlock, appendHtmlChunk, isFinalChunk, cleanChunkAnchorsForFinalHtml, validateFinalHtml, extractMemorySnapshot } from '../lib/htmlChunkAssembler';

import { useWorkshopChat, type MessageType } from '../hooks/useWorkshopChat';
import { WorkshopHeader } from '../components/workshoppage/WorkshopHeader';
import { ChatPanel } from '../components/workshoppage/ChatPanel';
import { WorkspacePanel } from '../components/workshoppage/WorkspacePanel';
import { type TabType } from '../types/workshop';

const DEFAULT_PROMPTS = [{ id: 1, name: "🎮 游戏化设计专家", category: "gen_slide" }];

export default function WorkshopPage() {
  const { stageId, lessonId, artifactId } = useParams();
  const slideId = parseInt(artifactId || '0');
  const navigate = useNavigate();
  const location = useLocation();
  
  const [backCourseId, setBackCourseId] = useState<number | null>(
    location.state?.backCourseId ? Number(location.state.backCourseId) : null
  );

  // State (Data)
  const [slide, setSlide] = useState<SlideDetail | null>(null);
  const [versions, setVersions] = useState<SlideVersionInfo[]>([]);
  const [prompts, setPrompts] = useState<any[]>([]);
  const [selectedPromptId, setSelectedPromptId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Resources State
  const [resources, setResources] = useState<CloudResource[]>([]);
  const [isResLoading, setIsResLoading] = useState(false);

  // State (UI)
  const [currentHtml, setCurrentHtml] = useState('');
  const [currentSideGuide, setCurrentSideGuide] = useState('');
  const [currentSideGuideContent, setCurrentSideGuideContent] = useState('');
  const [currentMemory, setCurrentMemory] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>('preview');
  const [isSaving, setIsSaving] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isAuditing, setIsAuditing] = useState(false);
  const [auditContents, setAuditContents] = useState<Record<number, string>>({});
  
  const auditContent = auditContents[slideId] || '';
  
  // State (Fullscreen)
  const [isFullscreen, setIsFullscreen] = useState(false);

  // State (Slide Context)
  const [slideContext, setSlideContext] = useState<SlideContextResponse | null>(null);

  const refreshSlideContent = async (messageType?: MessageType, aiResponseText?: string) => {
    try {
      // 🆕 记忆快照提取：适用于所有含 JSON 块的 AI 响应（主要是教案类 guide）
      if (aiResponseText) {
        const snapshot = extractMemorySnapshot(aiResponseText);
        if (snapshot) {
          const memoryJson = JSON.stringify(snapshot, null, 2);
          setCurrentMemory(memoryJson);
          // 自动保存到服务端
          try {
            // 先获取服务端最新数据，避免用闭包中的旧状态覆盖 HTML/side_guide
            const detailRes = await getSlideDetail(slideId);
            const saveRes = await saveSlideHtml(slideId, {
              html_code: detailRes.data.html_code || '',
              side_guide: detailRes.data.side_guide || '',
              side_guide_content: detailRes.data.side_guide_content || '',
              memory: memoryJson,
              is_final: detailRes.data.is_final,
            });
            setSlide(saveRes.data);
            console.log('✅ 记忆快照已自动保存到服务端');
          } catch (e) {
            console.error('记忆快照自动保存失败:', e);
          }
        }
      }

      if (messageType === 'html' && aiResponseText) {
        const newHtmlChunk = extractHtmlCodeBlock(aiResponseText);
        const existingHtml = currentHtml;

        const result = appendHtmlChunk(existingHtml, newHtmlChunk);

        if (result.ok) {
          setCurrentHtml(result.combinedText);

          if (result.isFinal) {
            const cleanHtml = cleanChunkAnchorsForFinalHtml(result.combinedText);
            const validation = validateFinalHtml(cleanHtml);

            if (validation.ok) {
              const res = await saveSlideHtml(slideId, {
                html_code: cleanHtml,
                side_guide: currentSideGuide,
                side_guide_content: currentSideGuideContent,
                memory: currentMemory,
                is_final: true
              });
              setSlide(res.data);
              const vRes = await getSlideVersions(slideId);
              setVersions(vRes.data);
              setCurrentHtml(cleanHtml);
              alert('✅ HTML代码已自动拼接并保存');
            } else {
              setCurrentHtml(result.combinedText);
              alert(`⚠️ HTML拼接完成，但校验未通过：\n\n${validation.errors.join('\n')}\n\n请手动检查后保存。`);
            }
          } else {
            alert(`📎 HTML分块已自动拼接（未完成，期待后续分块）\n${result.message}`);
          }
        } else {
          alert(`❌ HTML自动拼接失败：${result.error}\n\n请手动检查代码或重新生成。`);
        }
      } else {
        const res = await getSlideDetail(slideId);
        setSlide(res.data);
        setCurrentHtml(res.data.html_code || '');
        setCurrentSideGuide(res.data.side_guide || '');
        setCurrentSideGuideContent(res.data.side_guide_content || '');
        setCurrentMemory(res.data.memory || '');

        if (messageType && messageType !== 'other' && messageType !== 'html') {
          const typeNames: Record<string, string> = {
            design: '设计',
            guide: '页边教案'
          };
          alert(`✅ ${typeNames[messageType]}已自动保存`);
        }
      }
    } catch (e) {
      console.error('Failed to refresh slide content:', e);
    }
  };

  // Hook
  const { messages, setMessages, isChatting, sendMessage, clearAllMessages, currentReasoning, stopGeneration } = useWorkshopChat(slideId, refreshSlideContent, slideContext?.stage.course_id);

  // 1. 初始化数据
  useEffect(() => {
    if (!slideId) return;
    setIsLoading(true);
    
    Promise.all([
      getSlideDetail(slideId),
      getSysPrompts('gen_slide').catch(() => ({ data: [] })),
      getSlideVersions(slideId).catch(() => ({ data: [] })),
      getSlideCloudResources(slideId).catch(() => ({ data: [] })),
      getSlideContext(slideId).catch(() => null)
    ]).then(([slideRes, promptsRes, versionsRes, resRes, contextRes]) => {
      setSlide(slideRes.data);
      setCurrentHtml(slideRes.data.html_code || '');
      setCurrentSideGuide(slideRes.data.side_guide || '');
      setCurrentSideGuideContent(slideRes.data.side_guide_content || '');
      setCurrentMemory(slideRes.data.memory || '');
      setVersions(versionsRes.data || []);
      setResources(resRes.data || []);
      
      if (slideRes.data.audit) {
        setAuditContents(prev => ({ ...prev, [slideId]: slideRes.data.audit }));
      }
      
      if (contextRes) {
        setSlideContext(contextRes.data);
      }
      
      const p = promptsRes.data.length > 0 ? promptsRes.data : DEFAULT_PROMPTS;
      setPrompts(p);
      if (!selectedPromptId && p.length > 0) setSelectedPromptId(p[0].id);

    }).finally(() => setIsLoading(false));
  }, [slideId]);

  useEffect(() => {
    const handleFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  const refreshResources = async () => {
    setIsResLoading(true);
    try {
      const res = await getSlideCloudResources(slideId);
      setResources(res.data || []);
    } catch (e) { console.error(e); } 
    finally { setIsResLoading(false); }
  };

  // 2. Handlers
  const toggleFullscreen = (targetState: boolean) => {
    setIsFullscreen(targetState);
    if (targetState) {
      document.documentElement.requestFullscreen().catch((e) => {
        console.warn("Fullscreen request failed:", e);
      });
    } else {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    }
  };

  const handleApplyCode = async (code: string) => {
    setCurrentHtml(code);
    setActiveTab('preview');
    
    setIsSaving(true);
    try {
      const hasContent = code.trim() !== '';
      const res = await saveSlideHtml(slideId, { 
        html_code: code, 
        side_guide: currentSideGuide, 
        side_guide_content: currentSideGuideContent,
        memory: currentMemory,
        is_final: hasContent
      });
      setSlide(res.data);
      const vRes = await getSlideVersions(slideId);
      setVersions(vRes.data);
    } catch (e) { 
      console.error("Auto-save failed:", e);
    } finally { 
      setIsSaving(false); 
    }
  };

  const validateBeforeSave = useCallback(() => {
    const warnings: string[] = [];
    
    if (!currentSideGuideContent || currentSideGuideContent.trim() === '') {
      warnings.push('• 设计内容为空');
    }
    
    if (currentHtml && currentHtml.trim() !== '') {
      let html = currentHtml.trim();
      
      if (html.startsWith('```html')) {
        warnings.push('• 代码开头包含 ```html 标记，建议去除');
      }
      if (html.endsWith('```')) {
        warnings.push('• 代码结尾包含 ``` 标记，建议去除');
      }
      
      const doctypeCount = (html.match(/<!DOCTYPE html>/gi) || []).length;
      if (doctypeCount === 0) {
        warnings.push('• 代码缺少 <!DOCTYPE html> 声明');
      } else if (doctypeCount > 1) {
        warnings.push('• 代码包含多个 <!DOCTYPE html> 声明');
      }
      
      if (!html.includes('</html>')) {
        warnings.push('• 代码缺少 </html> 结束标签');
      }
    } else {
      warnings.push('• HTML代码为空');
    }
    
    if (!currentSideGuide || currentSideGuide.trim() === '') {
      warnings.push('• 页边教案为空');
    }
    
    return warnings;
  }, [currentHtml, currentSideGuide, currentSideGuideContent]);

  const handleSave = useCallback(async () => {
    const warnings = validateBeforeSave();
    
    if (warnings.length > 0) {
      const confirmMessage = `⚠️ 保存前检测到以下问题：\n\n${warnings.join('\n')}\n\n是否仍要保存？`;
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }
    
    setIsSaving(true);
    try {
      const hasContent = currentHtml.trim() !== '';
      const res = await saveSlideHtml(slideId, { 
        html_code: currentHtml, 
        side_guide: currentSideGuide, 
        side_guide_content: currentSideGuideContent,
        memory: currentMemory,
        is_final: hasContent
      });
      setSlide(res.data);
      const vRes = await getSlideVersions(slideId);
      setVersions(vRes.data);
      alert(hasContent ? "✅ 保存成功！" : "✅ 保存成功（内容为空，标记为草稿）！");
    } catch (e) { alert("❌ 保存失败"); } 
    finally { setIsSaving(false); }
  }, [slideId, currentHtml, currentSideGuide, currentSideGuideContent, currentMemory, validateBeforeSave]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleSave]);

  const handleNavigation = useCallback(async (dir: 'prev' | 'next') => {
    if (isNavigating) return;
    setIsNavigating(true);
    try {
      const res = await getAdjacentSlide(slideId, dir);
      if (res.data?.id) {
        navigate(`/workshop/ware/${stageId}/${lessonId}/${res.data.id}`, { 
          state: { backCourseId } 
        });
      }
    } catch (e: any) {
      if (e.response?.status === 404) {
        if (dir === 'next') {
          if (window.confirm("已是最后一页，是否创建新幻灯片？")) {
            try {
              const currentIndex = slideContext?.slide.slide_index || 0;
              const planId = slideContext?.slide.plan_id;
              if (planId) {
                const newSlide = await createNewSlide({ 
                  slide_index: currentIndex + 1, 
                  plan_id: planId 
                });
                navigate(`/workshop/ware/${stageId}/${lessonId}/${newSlide.data.id}`, { 
                  state: { backCourseId } 
                });
              }
            } catch (createError) {
              alert("创建幻灯片失败");
            }
          }
        } else {
          alert("第一页");
        }
      }
    } finally { setIsNavigating(false); }
  }, [isNavigating, slideId, stageId, lessonId, backCourseId, navigate, slideContext]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'NEXUS_NAVIGATE') {
        const dir = event.data.direction;
        if (dir === 'prev' || dir === 'next') {
          handleNavigation(dir);
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleNavigation]);

  const handleBack = () => {
    if (backCourseId) {
      navigate(`/course/${backCourseId}`);
    } else {
      navigate('/course/5');
    }
  };

  const handleDeleteMessage = async (msgId: number) => {
    if (!window.confirm("确定删除？")) return;
    try {
      const res = await deleteSlideChatMessage(msgId);
      
      // 找到被删除的AI消息的索引
      const aiMsgIndex = messages.findIndex(m => m.id === msgId);
      if (aiMsgIndex > 0) {
        // 如果AI消息前面有用户消息，也一并删除
        const userMsg = messages[aiMsgIndex - 1];
        if (userMsg.role === 'user') {
          setMessages(prev => prev.filter(m => 
            !res.data.deleted_ids.includes(m.id as number) && m.id !== userMsg.id
          ));
          return;
        }
      }
      
      // 只删除服务端返回的ID
      setMessages(prev => prev.filter(m => !res.data.deleted_ids.includes(m.id as number)));
    } catch (e) { alert("删除失败"); }
  };

  const handleClearHistory = async () => {
    if (messages.length === 0) return;
    if (!window.confirm("⚠️ 确定要清空所有聊天记录吗？\n\n此操作将删除左侧所有对话消息\n此操作不可逆！")) return;
    const success = await clearAllMessages();
    if (!success) alert("清空失败");
  };

  const handleClearSlide = async () => {
    if (!window.confirm("⚠️ 确定要清空当前 Slide 的所有内容吗？\n\n此操作将清空：\n• 设计内容\n• HTML代码\n• 页边教案\n• 教研记忆快照\n\n此操作不可逆！")) return;
    
    setIsClearing(true);
    try {
      const res = await clearSlideFields(slideId);
      setCurrentHtml('');
      setCurrentSideGuide('');
      setCurrentSideGuideContent('');
      setCurrentMemory('');
      setSlide(prev => prev ? { ...prev, html_code: '', side_guide: '', side_guide_content: '', memory: '' } : null);
      alert(`✅ 清空成功！已清空字段：${res.data.cleared_fields.join(', ')}`);
    } catch (e: any) {
      const msg = e.response?.data?.detail || e.message || "清空失败";
      alert(`❌ 清空失败: ${msg}`);
    } finally {
      setIsClearing(false);
    }
  };

  const handleExportSlide = async () => {
    const slideIndex = slide?.slide_index || 1;
    const prefix = `P${String(slideIndex).padStart(2, '0')}`;
    
    setIsExporting(true);
    try {
      if (!('showSaveFilePicker' in window)) {
        alert('您的浏览器不支持文件保存功能，请使用最新版 Chrome/Edge 浏览器');
        return;
      }

      const designContent = currentSideGuideContent || '';
      const htmlContent = currentHtml || '';
      const guideContent = currentSideGuide || '';
      const auditContentText = auditContent || '';

      const saveFile = async (defaultName: string, content: string, fileType: string) => {
        try {
          const handle = await (window as any).showSaveFilePicker({
            suggestedName: defaultName,
            types: [{
              description: fileType,
              accept: fileType === 'html' ? { 'text/html': ['.html'] } : { 'text/plain': ['.txt'] }
            }]
          });
          const writable = await handle.createWritable();
          await writable.write(content);
          await writable.close();
          return true;
        } catch (e: any) {
          if (e.name === 'AbortError') return false;
          throw e;
        }
      };

      let exportedCount = 0;
      
      if (designContent.trim()) {
        const success = await saveFile(`${prefix}设计.txt`, designContent, 'txt');
        if (success) exportedCount++;
      }
      
      if (htmlContent.trim()) {
        const success = await saveFile(`${prefix}代码.html`, htmlContent, 'html');
        if (success) exportedCount++;
      }
      
      if (guideContent.trim()) {
        const success = await saveFile(`${prefix}页边教案.txt`, guideContent, 'txt');
        if (success) exportedCount++;
      }

      if (auditContentText.trim()) {
        const success = await saveFile(`${prefix}审核.txt`, auditContentText, 'txt');
        if (success) exportedCount++;
      }

      if (exportedCount === 0) {
        alert('当前 Slide 没有可导出的内容');
      } else {
        alert(`✅ 成功导出 ${exportedCount} 个文件`);
      }
    } catch (e: any) {
      console.error('Export failed:', e);
      alert(`❌ 导出失败: ${e.message || '未知错误'}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleAudit = async () => {
    setIsAuditing(true);
    try {
      const courseId = slideContext?.stage.course_id;
      let promptId: number | undefined;
      if (courseId === 5) promptId = 6;
      else if (courseId === 6) promptId = 18;
      
      const res = await auditSlide(slideId, promptId);
      setAuditContents(prev => ({ ...prev, [slideId]: res.data.audit_content }));
      setActiveTab('audit');
      alert(`✅ 审核完成！\n输入Token: ${res.data.input_tokens}\n输出Token: ${res.data.output_tokens}\n费用: ¥${((res.data.input_money ?? 0) + (res.data.output_money ?? 0)).toFixed(4)}`);
    } catch (e: any) {
      const msg = e.response?.data?.detail || e.message || "审核失败";
      alert(`❌ 审核失败: ${msg}`);
    } finally {
      setIsAuditing(false);
    }
  };

  const handleDeleteResource = async (resourceId: number) => {
    try {
      await deleteOSSFile(resourceId);
      await refreshResources();
    } catch (e: any) {
      const msg = e.response?.data?.detail || e.message || "Unknown error";
      alert("Delete Failed: " + msg);
    }
  };

  const handleFileUpload = async (file: File) => {
    try {
      const res = await uploadToOSS(file, slideId);
      const data = (res as any).data ? (res as any).data : res;
      
      if (data && data.file_url) {
        await refreshResources(); 
        return data;
      } else {
        throw new Error("Invalid response");
      }
    } catch (e: any) { 
      let msg = "Unknown error";
      if (e.response?.data) {
        if (e.response.data.details) {
          msg = e.response.data.details.Message || e.response.data.details.Code || JSON.stringify(e.response.data.details);
        } else {
          msg = e.response.data.detail || e.response.data.message || JSON.stringify(e.response.data);
        }
      } else {
        msg = e.message || "Unknown error";
      }
      alert("Upload Failed: " + msg); 
      throw e; 
    }
  };

  // 🔥 Chat Handler Wrapper: 适配多模态参数
  const handleChatSend = (content: string, files: string[], model: 'gemini' | 'nanobanana', messageType: MessageType = 'other') => {
    if (selectedPromptId) {
      sendMessage(content, selectedPromptId, files, model, messageType);
    }
  };

  if (isLoading) return <div className="h-screen flex items-center justify-center"><Loader2 className="animate-spin"/></div>;

  return (
    <div className="h-screen flex flex-col bg-gray-100 overflow-hidden">
      <WorkshopHeader 
        slide={slide} versions={versions} isSaving={isSaving} isNavigating={isNavigating} isClearing={isClearing} isExporting={isExporting}
        slideContext={slideContext}
        onBack={handleBack} onSave={handleSave} onClear={handleClearSlide} onExport={handleExportSlide}
        onVersionChange={(e) => navigate(`/workshop/ware/${stageId}/${lessonId}/${e.target.value}`, { state: { backCourseId } })}
        onNavigation={handleNavigation}
      />
      <div className="flex-1 flex overflow-hidden">
        <ChatPanel 
          messages={messages} setMessages={setMessages} isChatting={isChatting} prompts={prompts} 
          selectedPromptId={selectedPromptId} onSelectPrompt={setSelectedPromptId}
          artifactId={slideId}
          slideContext={slideContext}
          onSendMessage={handleChatSend}
          onApplyCode={handleApplyCode}
          onDeleteMessage={handleDeleteMessage} 
          onClearHistory={handleClearHistory}
          onAudit={handleAudit}
          isAuditing={isAuditing}
          currentHtml={currentHtml}
          currentReasoning={currentReasoning}
          onStopGeneration={stopGeneration}
        />
        <WorkspacePanel 
          activeTab={activeTab} setActiveTab={setActiveTab}
          currentHtml={currentHtml} setCurrentHtml={setCurrentHtml}
          currentSideGuide={currentSideGuide} setCurrentSideGuide={setCurrentSideGuide}
          currentSideGuideContent={currentSideGuideContent} setCurrentSideGuideContent={setCurrentSideGuideContent}
          currentMemory={currentMemory} setCurrentMemory={setCurrentMemory}
          slide={slide} 
          slideId={slideId}
          onFileUpload={handleFileUpload}
          resources={resources}
          isResourcesLoading={isResLoading}
          onNavigation={handleNavigation}
          isFullscreen={isFullscreen}
          onToggleFullscreen={toggleFullscreen}
          onDeleteResource={handleDeleteResource}
          onSave={handleSave}
          auditContent={auditContent}
        />
      </div>
    </div>
  );
}
